import type { ReactElement } from "react";
import type { GridRowParams } from "@mui/x-data-grid";
import type { TooltipProps } from "@mui/material/Tooltip";
import type { Entity, ID, ResourceResponse } from "types";
import { MenuItem } from "consts/openBanking";
import type { TriggerableDialogProps } from "ui/molecules/Dialog";
import type { ActionPanelProps } from "ui/molecules/ActionPanel";
import type { TableProps } from "ui/widgets/Table";

type PivotActionHandler<
    TPivotEntity extends Entity
> = (pivot: Pick<TPivotEntity, 'pki'>) => Promise<ResourceResponse<unknown>>;

export type WithGridRowParams<T = {}> =
    & T
    & GridRowParams
    & {
        readonly pivotId?: ID;
    };

export type OpenBankingResourcePivotActionProps<TPivotEntity extends Entity> = {
    readonly getOpenBankingResourcePivotActionProps: (params: WithGridRowParams) => {
        readonly TriggerComponentProps: {
            readonly icon?: ReactElement;
            readonly label?: string;
            readonly color?: 'error' | 'success';
            readonly TooltipProps: Partial<Omit<TooltipProps, 'children'>>;
        };
        readonly ActionPanelProps: Omit<ActionPanelProps, 'onSave'> & {
            readonly onSave: PivotActionHandler<TPivotEntity>;
        };
        readonly isLoading?: boolean;
        readonly onSuccess?: (payload: unknown) => void;
    } & Pick<TriggerableDialogProps, 'ModalProps'>;
};

export type ManageOpenBankingResourcePivotProps<
    TResource extends Entity,
    TPivotEntity extends Entity
> = {
    readonly TableProps: Omit<TableProps,
        | 'selectIsTableLoading'
        | 'selectIsTableUninitialized'
        | 'selectTableDataSlice'
    >;
    readonly menuItem: MenuItem;
    readonly groupBy: (pivotEntity: TPivotEntity) => ID;
    readonly fetchPivots: (resource: Pick<TResource, 'pki'>) => Promise<Response>;
    readonly AssociateActionProps: OpenBankingResourcePivotActionProps<TPivotEntity>;
    readonly DissassociateActionProps: OpenBankingResourcePivotActionProps<TPivotEntity>;
    readonly resource?: Pick<TResource, 'pki'> | null;
};

export const enum ManageOpenBankingResourcePivotClassNames {
    PivotActive = 'MuiDataGrid-row--pivot-active'
};
